import { Canvas } from "@react-three/fiber";
import { OrbitControls, PerspectiveCamera, Stars } from "@react-three/drei";
import { EffectComposer, Bloom } from "@react-three/postprocessing";
import { DroneField, type CymDrive } from "../DroneField";
import { METER, NeighborField, type PhysicsParams } from "../NeighborField";
import { GroundPlane } from "./GroundPlane";
import { VisualizerField } from "../VisualizerField";
import { ShowPlayer } from "../ShowPlayer";
import type { VisStyle } from "../source";
import { SequencerField } from "./SequencerField";
import { FpvRig } from "./FpvRig";
import { ReplayRig } from "./ReplayRig";
import { FisheyeFx } from "./FisheyeFx";
import { ConstrainRig } from "./ConstrainRig";
import { AudioSampler } from "./AudioSampler";
import type { ShowMeta } from "../showFormat";
import type { Cloud } from "../formations";
import type { AudioFX } from "../audioFx";
import type { PhysicsMode } from "../hooks/usePhysics";
import type { FpvParams, Telemetry } from "../hooks/useFpv";
import type { useRadio } from "../hooks/useRadio";
import type { useSequencer } from "../hooks/useSequencer";
import type { useFlightTape } from "../hooks/useFlightTape";

type Props = {
  cloud: Cloud;
  sourceScale: number;
  physicsMode: PhysicsMode;
  physicsParams: PhysicsParams;
  show: ShowMeta | null;
  playing: boolean;
  speed: number;
  playhead: React.MutableRefObject<number>;
  sliderRef: React.RefObject<HTMLInputElement | null>;
  brightness: number;
  bloom: boolean;
  bloomStrength: number;
  autoRotate: boolean;
  rotateSpeed: number; // °/s
  constrainView: boolean;
  fpvActive: boolean;
  fpvParams: FpvParams;
  fpvTelemetry: React.MutableRefObject<Telemetry>;
  radio: ReturnType<typeof useRadio>;
  tape: ReturnType<typeof useFlightTape>;
  sequencerActive: boolean;
  sequencer: ReturnType<typeof useSequencer>;
  count: number;
  cymDrive: CymDrive | null;
  fx: AudioFX;
  flowDrive: { speed: number } | null;
  visStyle: VisStyle | null;
};

/** The WebGL scene: camera, controls, starfield, the active swarm, and bloom. */
export function StudioScene({
  cloud, sourceScale, physicsMode, physicsParams, show, playing, speed, playhead, sliderRef, brightness, bloom, bloomStrength,
  autoRotate, rotateSpeed, constrainView, fpvActive, fpvParams, fpvTelemetry, radio, tape, sequencerActive, sequencer, count, cymDrive, fx,
  flowDrive, visStyle,
}: Props) {
  // a playing tape owns the camera like FPV does; its meta restores the lens + floor
  const replaying = tape.replaying && !!tape.tape;
  const groundOn = fpvActive ? fpvParams.ground : replaying ? tape.tape!.meta.ground : false;
  const groundAlt = fpvActive ? fpvParams.groundAlt : tape.tape?.meta.groundAlt ?? 12;
  const groundDark = fpvActive ? fpvParams.groundDark : tape.tape?.meta.groundDark ?? 0.35;
  const lensOn = fpvActive || replaying;
  const lensStrength = fpvActive ? fpvParams.fisheye : tape.tape?.meta.fisheye ?? 0;
  return (
    <Canvas
      gl={{ antialias: true, alpha: false, powerPreference: "high-performance" }}
      dpr={Math.min(window.devicePixelRatio, 2)}
    >
      <color attach="background" args={["#03040a"]} />
      <PerspectiveCamera makeDefault position={[0, 1.5, 12]} fov={50} near={0.1} far={400} />
      {!fpvActive && !replaying && (
        <OrbitControls
          enabled={!constrainView} // constrain owns the camera while on
          enableDamping
          dampingFactor={0.08}
          minDistance={2}
          maxDistance={80}
          target={[0, 0, 0]}
          autoRotate={autoRotate && !constrainView}
          autoRotateSpeed={rotateSpeed / 6} // drei units: 2.0 = 12°/s, so °/s ÷ 6
        />
      )}
      <ConstrainRig active={constrainView && !fpvActive && !replaying} />
      <FpvRig active={fpvActive} params={fpvParams} telemetry={fpvTelemetry} radio={radio} tape={tape} />
      <ReplayRig tape={tape.tape} active={replaying} loop={tape.loop} onEnd={tape.stopReplay} sticksOut={tape.replaySticks} />
      <Stars radius={160} depth={70} count={2400} factor={3} saturation={0} fade speed={0.4} />
      {groundOn && <GroundPlane y={-groundAlt * METER} dark={groundDark} />}
      <AudioSampler />
      {sequencerActive ? (
        <SequencerField
          key={count}
          timeline={sequencer.timeline}
          timing={sequencer.timing}
          playhead={sequencer.playhead}
          playing={sequencer.playing}
          loop={sequencer.loop}
          count={count}
          cloudsRef={sequencer.cloudsRef}
          cloudsVersion={sequencer.cloudsVersion}
          brightness={brightness}
          fx={fx}
        />
      ) : show ? (
        <ShowPlayer show={show} playing={playing} speed={speed} playhead={playhead} sliderRef={sliderRef} brightness={brightness} />
      ) : visStyle ? (
        <VisualizerField key={count} count={count} style={visStyle} brightness={brightness} fx={fx} />
      ) : (
        physicsMode === "neighbor" ? (
          <NeighborField key={cloud.positions.length} cloud={cloud} params={physicsParams} brightness={brightness} scale={cymDrive ? 1 : sourceScale} cym={cymDrive} fx={fx} flow={flowDrive} />
        ) : (
          <DroneField key={cloud.positions.length} cloud={cloud} brightness={brightness} cym={cymDrive} scale={cymDrive ? 1 : sourceScale} fx={fx} />
        )
      )}
      {(bloom || lensOn) && (
        <EffectComposer>
          {[
            // intensity stays a PROP — a ref on a postprocessing effect crashes the Canvas (63d80ab)
            ...(bloom ? [<Bloom key="bloom" intensity={bloomStrength} luminanceThreshold={0.15} luminanceSmoothing={0.85} mipmapBlur radius={0.72} />] : []),
            // mounted whenever FPV/replay is on (uK 0 = identity) so slider drags never rebuild the composer
            ...(lensOn ? [<FisheyeFx key="fisheye" strength={lensStrength} />] : []),
          ]}
        </EffectComposer>
      )}
    </Canvas>
  );
}
